export const CAMPAIGN_MEDIA_ICON = "icon"
export const CAMPAIGN_MEDIA_IMAGE = "image"
export const CLASS_EMAIL_ICON = "campaign-email-icon"
export const CLASS_EMAIL_IMAGE = "campaign-email-image"

/** Email layout templates the super admin can pick for a campaign */
export const CAMPAIGN_TEMPLATE_DEFAULT = "default"
export const CAMPAIGN_TEMPLATE_BILINGUAL_SPLIT = "bilingual_split"

export const CTA_BUTTON_CLASS = "campaign-cta-button"
const DEFAULT_ICON_SIZE_PX = 48
const MAX_ICON_SIZE_PX = 128

const EDITOR_CHROME_SELECTORS =
  ".editor-image-remove-btn, .editor-icon-remove-btn, .editor-media-drag-handle, .editor-media-resize-handle, .editor-media-drop-marker"

const isCampaignCtaAnchor = (anchor) => {
  if (!anchor || anchor.tagName !== "A") return false
  if (anchor.closest(".editor-image-wrapper, .editor-icon-wrapper")) return false
  if (
    anchor.classList.contains(CTA_BUTTON_CLASS) ||
    anchor.classList.contains("campaign-editor-button") ||
    anchor.classList.contains("preview-button") ||
    anchor.getAttribute("data-campaign-cta") === "true"
  ) {
    return true
  }
  if (anchor.closest(".campaign-cta-button-wrapper")) return true
  return false
}

const isLikelyStandaloneCta = (anchor) => {
  const href = anchor.getAttribute("href") || ""
  if (!href || href.startsWith("#") || anchor.querySelector("img")) return false
  const parent = anchor.parentElement
  if (!parent || !["P", "DIV"].includes(parent.tagName)) return false
  const meaningful = Array.from(parent.childNodes).filter(
    (n) => !(n.nodeType === Node.TEXT_NODE && !n.textContent?.trim()),
  )
  if (meaningful.length !== 1 || meaningful[0] !== anchor) return false
  const label = anchor.textContent?.trim() || ""
  return label.length > 0 && label.length <= 80
}

const applyCtaButtonClasses = (anchor) => {
  anchor.classList.add("preview-button", CTA_BUTTON_CLASS, "campaign-editor-button")
  anchor.setAttribute("data-campaign-cta", "true")
  anchor.setAttribute("contenteditable", "false")
  anchor.removeAttribute("style")
}

const ensureCtaButtonWrapper = (anchor) => {
  if (anchor.closest(".campaign-cta-button-wrapper")) return

  let alignment = "left"
  const parent = anchor.parentElement
  if (parent) {
    const align = (parent.style?.textAlign || window.getComputedStyle?.(parent)?.textAlign || "").toLowerCase()
    if (align === "center" || align === "right") alignment = align
  }

  const wrapper = document.createElement("div")
  wrapper.className = `campaign-cta-button-wrapper campaign-cta-align-${alignment}`
  wrapper.setAttribute("contenteditable", "false")
  wrapper.style.textAlign = alignment
  wrapper.style.margin = "10px 0"

  anchor.parentNode.insertBefore(wrapper, anchor)
  wrapper.appendChild(anchor)
}

const parseTextAlign = (value) => {
  const align = String(value || "").toLowerCase()
  if (align === "center" || align === "right") return align
  return "left"
}

const getAlignmentFromClasses = (el) => {
  if (!el?.classList) return null
  if (el.classList.contains("campaign-cta-align-center")) return "center"
  if (el.classList.contains("campaign-cta-align-right")) return "right"
  if (el.classList.contains("campaign-cta-align-left")) return "left"
  return null
}

const getWrapperAlignment = (wrapper) => {
  const fromClass = getAlignmentFromClasses(wrapper)
  if (fromClass) return fromClass
  const fromStyle = parseTextAlign(wrapper.style?.textAlign)
  if (fromStyle !== "left" || wrapper.style?.textAlign) return fromStyle
  const fromAttr = wrapper.getAttribute?.("data-campaign-cta-align")
  if (fromAttr) return parseTextAlign(fromAttr)
  return "left"
}

const getCtaRowAlignment = (row) => {
  const fromClass = getAlignmentFromClasses(row)
  if (fromClass) return fromClass
  const fromData = row.getAttribute?.("data-campaign-cta-align")
  if (fromData) return parseTextAlign(fromData)
  const styleAttr = row.getAttribute?.("style") || ""
  const match = styleAttr.match(/text-align\s*:\s*(left|center|right)/i)
  if (match) return parseTextAlign(match[1])
  return parseTextAlign(row.style?.textAlign)
}

const unwrapMediaWrappers = (root) => {
  root.querySelectorAll(EDITOR_CHROME_SELECTORS).forEach((el) => el.remove())

  root.querySelectorAll(".editor-image-wrapper, .editor-icon-wrapper").forEach((wrapper) => {
    const img = wrapper.querySelector("img")
    if (!img?.parentNode) {
      wrapper.remove()
      return
    }

    const cleanImg = document.createElement("img")
    cleanImg.src = img.src || ""
    const icon = isIconMedia(wrapper, img)

    if (icon) {
      applySavedIconStyles(cleanImg)
    } else {
      applySavedImageStyles(cleanImg, img)
    }

    wrapper.parentNode.insertBefore(cleanImg, wrapper)
    wrapper.remove()
  })

  root.querySelectorAll("img").forEach((img) => {
    if (img.getAttribute("data-campaign-media")) return
    if (img.classList.contains(CLASS_EMAIL_ICON) || img.classList.contains("editor-icon")) {
      applySavedIconStyles(img)
    } else if (!img.classList.contains(CLASS_EMAIL_IMAGE)) {
      applySavedImageStyles(img, img)
    }
  })
}

const flattenButtonWrappers = (root) => {
  root.querySelectorAll(".campaign-cta-button-wrapper").forEach((wrapper) => {
    const anchor = wrapper.querySelector("a[href]")
    if (!anchor?.parentNode) {
      wrapper.remove()
      return
    }

    applyCtaButtonClasses(anchor)
    const alignment = getWrapperAlignment(wrapper)
    const row = document.createElement("p")
    row.className = `campaign-cta-row campaign-cta-align-${alignment}`
    row.style.textAlign = alignment
    row.style.margin = "12px 0"
    row.appendChild(anchor)
    wrapper.replaceWith(row)
  })

  root.querySelectorAll("a[href]").forEach((anchor) => {
    if (!isCampaignCtaAnchor(anchor) && !isLikelyStandaloneCta(anchor)) return
    if (anchor.closest(".campaign-cta-row")) return
    // Keep pasted HTML template buttons (already have inline styles) as-is
    if (!isCampaignCtaAnchor(anchor) && anchor.getAttribute("style")) return
    applyCtaButtonClasses(anchor)
    const row = document.createElement("p")
    row.className = "campaign-cta-row campaign-cta-align-left"
    row.style.textAlign = "left"
    row.style.margin = "12px 0"
    anchor.parentNode.insertBefore(row, anchor)
    row.appendChild(anchor)
  })
}

const softenHorizontalRules = (root) => {
  root.querySelectorAll("hr").forEach((hr) => {
    hr.replaceWith(document.createElement("br"))
  })
}

const EMAIL_BLANK_LINE_CLASS = "campaign-blank-line"
const EMAIL_BLANK_LINE_STYLE = "margin:0;height:16px;line-height:16px;font-size:16px;"

/**
 * Blank lines typed in the editor (Enter twice) arrive as empty <p>/<div><br></div>.
 * Email clients collapse those to zero height, so turn them into fixed-height
 * spacers. Leading/trailing blanks are dropped so they don't pad the block.
 */
const preserveBlankLines = (root) => {
  root.querySelectorAll("p, div").forEach((el) => {
    if (!root.contains(el)) return
    const hasContent =
      el.textContent?.replace(/\u00A0/g, " ").trim() ||
      el.querySelector("img, a")
    if (hasContent) return
    const spacer = document.createElement("p")
    spacer.className = EMAIL_BLANK_LINE_CLASS
    spacer.setAttribute("style", EMAIL_BLANK_LINE_STYLE)
    spacer.innerHTML = "&nbsp;"
    el.replaceWith(spacer)
  })

  const isEdgeBlank = (node) =>
    (node?.nodeType === Node.ELEMENT_NODE && node.classList.contains(EMAIL_BLANK_LINE_CLASS)) ||
    (node?.nodeType === Node.TEXT_NODE && !node.textContent?.trim())
  while (isEdgeBlank(root.firstChild)) root.firstChild.remove()
  while (isEdgeBlank(root.lastChild)) root.lastChild.remove()
}

export const EMAIL_CTA_BUTTON_STYLE =
  "display:inline-block;box-sizing:border-box;min-width:140px;height:44px;line-height:20px;padding:12px 24px;background:linear-gradient(90deg,#343C3E 0%,#308AA3 100%);color:#ffffff;text-decoration:none;font-size:14px;font-weight:600;border-radius:8px;border:none;text-align:center;white-space:nowrap;"

export const buildCtaRowStyle = (alignment) =>
  `text-align:${alignment};margin:12px 0;`

/** Inline styles + data attrs so email clients and backend get alignment/styling in payload */
const applyEmailInlineStyles = (root) => {
  root.querySelectorAll(".campaign-cta-button-wrapper").forEach((wrapper) => {
    const anchor = wrapper.querySelector("a[href]")
    if (!anchor) return
    const alignment = getWrapperAlignment(wrapper)
    wrapper.setAttribute("data-campaign-cta-align", alignment)
    wrapper.setAttribute("style", buildCtaRowStyle(alignment))
  })

  root.querySelectorAll("p.campaign-cta-row").forEach((row) => {
    const alignment = getCtaRowAlignment(row)
    row.classList.add(`campaign-cta-align-${alignment}`)
    row.setAttribute("data-campaign-cta-align", alignment)
    row.setAttribute("align", alignment)
    row.setAttribute("style", buildCtaRowStyle(alignment))
  })

  root.querySelectorAll("a.campaign-cta-button, a[data-campaign-cta]").forEach((anchor) => {
    const row = anchor.closest("p.campaign-cta-row, .campaign-cta-button-wrapper")
    const alignment = row ? getCtaRowAlignment(row) || getWrapperAlignment(row) : "left"
    anchor.setAttribute("style", EMAIL_CTA_BUTTON_STYLE)
    anchor.setAttribute("data-campaign-cta-style", EMAIL_CTA_BUTTON_STYLE)
    anchor.setAttribute("data-campaign-cta-align", alignment)
  })
}

/** Extract CTA buttons (label, link, alignment, inline styles) from serialized description HTML */
export const extractCtaButtonsFromHtml = (html) => {
  if (!html) return []

  try {
    const root = document.createElement("div")
    root.innerHTML = html
    const seen = new Set()
    const buttons = []

    root.querySelectorAll("a.campaign-cta-button, a[data-campaign-cta]").forEach((anchor) => {
      const href = anchor.getAttribute("href") || ""
      const label = anchor.textContent?.trim() || ""
      const key = `${href}::${label}`
      if (!href || !label || seen.has(key)) return
      seen.add(key)

      const row = anchor.closest("p.campaign-cta-row, .campaign-cta-button-wrapper")
      const alignment = row
        ? getCtaRowAlignment(row) || getWrapperAlignment(row)
        : parseTextAlign(anchor.getAttribute("data-campaign-cta-align"))

      buttons.push({
        label,
        link: href,
        alignment,
        style: anchor.getAttribute("style") || EMAIL_CTA_BUTTON_STYLE,
        row_style: row?.getAttribute("style") || buildCtaRowStyle(alignment),
      })
    })

    return buttons
  } catch {
    return []
  }
}

/** Build email_block with HTML descriptions and explicit CTA styling/alignment metadata */
export const buildCampaignEmailBlock = (formData) => {
  const emailBlock = {}

  if (formData.greetingEnglish?.trim()) {
    emailBlock.greeting_en = formData.greetingEnglish.trim()
  }
  if (formData.descriptionEnglish?.trim()) {
    emailBlock.text_en = serializeDescriptionForEmail(formData.descriptionEnglish.trim())
    const ctas = extractCtaButtonsFromHtml(emailBlock.text_en)
    if (ctas.length) emailBlock.cta_buttons_en = ctas
  }
  if (formData.greetingArabic?.trim()) {
    emailBlock.greeting_ar = formData.greetingArabic.trim()
  }
  if (formData.descriptionArabic?.trim()) {
    emailBlock.text_ar = serializeDescriptionForEmail(formData.descriptionArabic.trim())
    const ctas = extractCtaButtonsFromHtml(emailBlock.text_ar)
    if (ctas.length) emailBlock.cta_buttons_ar = ctas
  }
  if (formData.image?.trim()) {
    emailBlock.image_url = formData.image.trim()
  }
  if (formData.template === CAMPAIGN_TEMPLATE_BILINGUAL_SPLIT) {
    emailBlock.template = CAMPAIGN_TEMPLATE_BILINGUAL_SPLIT
    if (formData.headerImage?.trim()) {
      emailBlock.header_image_url = formData.headerImage.trim()
    }
    if (formData.footerImage?.trim()) {
      emailBlock.footer_image_url = formData.footerImage.trim()
    }
  }
  if (formData.buttonLink?.trim()) {
    emailBlock.button_link = formData.buttonLink.trim()
    emailBlock.button_style = EMAIL_CTA_BUTTON_STYLE
  }
  if (formData.buttonText?.trim()) {
    emailBlock.button_label_en = formData.buttonText.trim()
    if (formData.buttonLink?.trim() && !emailBlock.cta_buttons_en?.length) {
      emailBlock.button_alignment_en = "left"
      emailBlock.button_style_en = EMAIL_CTA_BUTTON_STYLE
    }
  }
  if (formData.buttonTextArabic?.trim()) {
    emailBlock.button_label_ar = formData.buttonTextArabic.trim()
    if (formData.buttonLink?.trim() && !emailBlock.cta_buttons_ar?.length) {
      emailBlock.button_alignment_ar = "right"
      emailBlock.button_style_ar = EMAIL_CTA_BUTTON_STYLE
    }
  }

  return emailBlock
}

export const normalizeCampaignButtonsForEditor = (root) => {
  if (!root) return

  root.querySelectorAll("a[href]").forEach((anchor) => {
    if (!isCampaignCtaAnchor(anchor) && !isLikelyStandaloneCta(anchor)) return
    applyCtaButtonClasses(anchor)
    ensureCtaButtonWrapper(anchor)
  })
}

/** True when a string looks like HTML markup (tags), not plain prose */
export const looksLikeHtmlSource = (value) => {
  if (!value || typeof value !== "string") return false
  const trimmed = value.trim()
  if (!trimmed) return false

  // Escaped HTML entities that represent tags
  if (/&lt;\/?[a-zA-Z][^&]*&gt;/.test(trimmed)) return true

  // Raw HTML tags / full documents / common email layout tags
  if (/<\/?[a-zA-Z][^>]*>/.test(trimmed)) return true

  return false
}

/** Decode HTML entities without parsing tags (safe for mixed editor HTML) */
export const unescapeHtmlEntities = (value) => {
  if (!value) return ""
  return String(value)
    .replace(/&nbsp;/gi, " ")
    .replace(/&quot;/g, '"')
    .replace(/&#39;|&apos;/g, "'")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&amp;/g, "&")
}

/**
 * Serialize a live editor <img> (or wrapper) into an email-safe <img> tag string.
 */
const serializeLiveImageToTag = (img) => {
  if (!img?.src) return ""
  const icon =
    img.getAttribute("data-campaign-media") === CAMPAIGN_MEDIA_ICON ||
    img.classList.contains(CLASS_EMAIL_ICON) ||
    img.classList.contains("editor-icon") ||
    img.closest?.(".editor-icon-wrapper")

  if (icon) {
    return `<img src="${img.src}" alt="${img.alt || "Icon"}" class="${CLASS_EMAIL_ICON}" data-campaign-media="${CAMPAIGN_MEDIA_ICON}" width="${DEFAULT_ICON_SIZE_PX}" style="width:${DEFAULT_ICON_SIZE_PX}px;height:auto;max-width:${MAX_ICON_SIZE_PX}px;display:inline-block;vertical-align:middle;margin:4px 8px 4px 0;" />`
  }

  const width = img.style?.width || ""
  const widthAttr = parseInt(width, 10)
  const widthStyle = width ? `width:${width};` : ""
  const widthHtml = widthAttr > 0 ? ` width="${widthAttr}"` : ""
  return `<img src="${img.src}" alt="${img.alt || "Image"}" class="${CLASS_EMAIL_IMAGE}" data-campaign-media="${CAMPAIGN_MEDIA_IMAGE}"${widthHtml} style="${widthStyle}max-width:100%;height:auto;display:block;margin:8px 0;" />`
}

/**
 * When HTML source is pasted into contenteditable, the browser wraps each line
 * in <div>/<br> and stores tags as text. Rebuild the original source from
 * textContent so tags like <a ...> are not split/broken.
 * Also keeps toolbar-inserted images/buttons that would otherwise be dropped.
 */
const extractHtmlSourceFromEditorValue = (html) => {
  const probe = document.createElement("div")
  probe.innerHTML = html

  const hasEscapedTags = /&lt;\/?[a-zA-Z]/.test(html)
  if (!hasEscapedTags) {
    // Plain HTML string with no real element tree (or only entity text)
    const liveMeaningful = probe.querySelector(
      "p, div, h1, h2, h3, h4, h5, h6, img, a, table, ul, ol, span, br",
    )
    if (!liveMeaningful && looksLikeHtmlSource(html)) {
      const decoded = unescapeHtmlEntities(html).trim()
      if (looksLikeHtmlSource(decoded)) return decoded
    }
    return null
  }

  // Strip editor-only chrome so "×" / handles never leak into preview
  probe.querySelectorAll(EDITOR_CHROME_SELECTORS).forEach((el) => el.remove())

  // Preserve live images inserted via toolbar (textContent would drop them)
  const imageTokens = []
  probe
    .querySelectorAll(".editor-image-wrapper, .editor-icon-wrapper, img")
    .forEach((node) => {
      const img =
        node.tagName === "IMG" ? node : node.querySelector?.("img")
      if (!img?.src) {
        node.remove?.()
        return
      }
      // Skip imgs already handled inside a wrapper we will replace
      if (
        node.tagName === "IMG" &&
        node.closest(".editor-image-wrapper, .editor-icon-wrapper")
      ) {
        return
      }
      const token = `__CAMPAIGN_IMG_${imageTokens.length}__`
      imageTokens.push(serializeLiveImageToTag(img))
      const placeholder = document.createTextNode(`\n${token}\n`)
      const target =
        node.classList?.contains("editor-image-wrapper") ||
        node.classList?.contains("editor-icon-wrapper")
          ? node
          : img
      target.parentNode?.replaceChild(placeholder, target)
    })

  // Preserve live CTA buttons inserted via toolbar
  const buttonTokens = []
  probe
    .querySelectorAll(
      "a.campaign-cta-button, a[data-campaign-cta], .campaign-cta-button-wrapper",
    )
    .forEach((node) => {
      const anchor =
        node.tagName === "A" ? node : node.querySelector?.("a[href]")
      if (!anchor?.getAttribute("href")) {
        node.remove?.()
        return
      }
      if (
        node.tagName !== "A" &&
        node.classList?.contains("campaign-cta-button-wrapper")
      ) {
        // ok
      } else if (
        node.tagName === "A" &&
        node.closest(".campaign-cta-button-wrapper")
      ) {
        return
      }
      const href = anchor.getAttribute("href") || ""
      const label = (anchor.textContent || "").trim() || "Button"
      const style = anchor.getAttribute("style") || EMAIL_CTA_BUTTON_STYLE
      const token = `__CAMPAIGN_BTN_${buttonTokens.length}__`
      buttonTokens.push(
        `<p style="text-align:left;margin:12px 0;"><a href="${href}" style="${style}">${label}</a></p>`,
      )
      const placeholder = document.createTextNode(`\n${token}\n`)
      const target = node.classList?.contains("campaign-cta-button-wrapper")
        ? node
        : anchor
      target.parentNode?.replaceChild(placeholder, target)
    })

  let text = (probe.textContent || "").replace(/\u00A0/g, " ").trim()
  if (!text || !/<\/?[a-zA-Z][^>]*>/.test(text)) {
    // No HTML source text — but may still have only images
    if (!imageTokens.length && !buttonTokens.length) return null
    text = imageTokens.concat(buttonTokens).join("\n")
  } else {
    imageTokens.forEach((tag, i) => {
      text = text.replace(`__CAMPAIGN_IMG_${i}__`, tag)
    })
    buttonTokens.forEach((tag, i) => {
      text = text.replace(`__CAMPAIGN_BTN_${i}__`, tag)
    })
  }

  return text
}

/**
 * If the editor/API stored HTML as escaped source (visible code), turn it into
 * real markup so preview and outbound email render the design — not the tags.
 */
export const coerceHtmlSourceToMarkup = (html) => {
  if (!html) return ""

  const value = String(html).trim()
  if (!value) return ""

  const asSource = extractHtmlSourceFromEditorValue(value)
  if (asSource) {
    const docMatch = asSource.match(/<body[^>]*>([\s\S]*?)<\/body>/i)
    return (docMatch ? docMatch[1] : asSource).trim()
  }

  return value
}

const flattenDescriptionHtml = (html) => {
  if (!html) return ""

  const normalized = coerceHtmlSourceToMarkup(html)
  const tempDiv = document.createElement("div")
  tempDiv.innerHTML = normalized

  unwrapMediaWrappers(tempDiv)
  flattenButtonWrappers(tempDiv)
  softenHorizontalRules(tempDiv)
  preserveBlankLines(tempDiv)
  applyEmailInlineStyles(tempDiv)

  tempDiv
    .querySelectorAll("[contenteditable]")
    .forEach((el) => el.removeAttribute("contenteditable"))

  return tempDiv.innerHTML
}

/**
 * Single canonical HTML for email body + in-app preview.
 * Strips editor chrome, unwraps media/buttons, applies inline email-safe styles.
 * Also converts pasted HTML *source code* into real markup so emails send as design.
 */
export const serializeDescriptionForEmail = (html) => {
  if (!html) return ""
  try {
    return flattenDescriptionHtml(html)
  } catch {
    return html
  }
}

/** @deprecated Use serializeDescriptionForEmail — kept for existing imports */
export const sanitizeEditorHtml = serializeDescriptionForEmail

/** Parse stored text_en / text_ar (HTML or legacy XML) from API */
export const parseStoredEmailDescription = (stored) => {
  if (!stored) return ""

  try {
    const value = coerceHtmlSourceToMarkup(String(stored).trim())
    if (
      value.includes("<p") ||
      value.includes("<div") ||
      value.includes("<br") ||
      value.includes("<img") ||
      value.includes("<a ") ||
      value.includes("<table") ||
      value.includes("<span")
    ) {
      return value
    }

    const parser = new DOMParser()
    const xmlDoc = parser.parseFromString(`<root>${value}</root>`, "text/xml")
    const parserError = xmlDoc.querySelector("parsererror")
    if (parserError) {
      return `<p>${value}</p>`
    }
    const root = xmlDoc.querySelector("root")
    return root?.innerHTML || value
  } catch {
    return stored
  }
}

const isIconMedia = (wrapper, img) => {
  if (wrapper?.classList?.contains("editor-icon-wrapper")) return true
  if (img?.getAttribute("data-campaign-media") === CAMPAIGN_MEDIA_ICON) return true
  if (img?.classList?.contains(CLASS_EMAIL_ICON) || img?.classList?.contains("editor-icon")) {
    return true
  }
  return false
}

const applySavedIconStyles = (cleanImg) => {
  const widthPx = DEFAULT_ICON_SIZE_PX
  cleanImg.className = CLASS_EMAIL_ICON
  cleanImg.setAttribute("data-campaign-media", CAMPAIGN_MEDIA_ICON)
  cleanImg.alt = "Icon"
  cleanImg.style.width = `${widthPx}px`
  cleanImg.style.height = "auto"
  cleanImg.style.maxWidth = `${MAX_ICON_SIZE_PX}px`
  cleanImg.style.maxHeight = `${MAX_ICON_SIZE_PX}px`
  cleanImg.style.display = "inline-block"
  cleanImg.style.verticalAlign = "middle"
  cleanImg.style.margin = "4px 8px 4px 0"
  cleanImg.setAttribute("width", String(widthPx))
}

const applySavedImageStyles = (cleanImg, sourceImg) => {
  cleanImg.className = CLASS_EMAIL_IMAGE
  cleanImg.setAttribute("data-campaign-media", CAMPAIGN_MEDIA_IMAGE)
  cleanImg.alt = sourceImg.alt || "Image"
  if (sourceImg.style.width) cleanImg.style.width = sourceImg.style.width
  const widthPx = parseInt(sourceImg.style.width, 10)
  if (widthPx > 0) cleanImg.setAttribute("width", String(widthPx))
  cleanImg.style.maxWidth = "100%"
  cleanImg.style.height = "auto"
  cleanImg.style.display = "block"
  cleanImg.style.margin = "8px 0"
}

export const descriptionHasCtaButton = (html) =>
  Boolean(
    html &&
      (html.includes("campaign-cta-button") ||
        html.includes("campaign-editor-button") ||
        html.includes("data-campaign-cta")),
  )

export const descriptionHasInlineImage = (html) =>
  Boolean(html && /<img[\s>]/i.test(html))

export const descriptionHasText = (html) => {
  if (!html || !String(html).trim()) return false
  const text = String(html)
    .replace(/<br\s*\/?>/gi, " ")
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/gi, " ")
    .trim()
  return text.length > 0
}

/** True when description has text, images, icons, or CTA buttons */
export const descriptionHasContent = (html) => {
  if (!html || !String(html).trim()) return false
  if (descriptionHasText(html)) return true
  if (descriptionHasInlineImage(html)) return true
  if (descriptionHasCtaButton(html)) return true
  if (
    /editor-image-wrapper|editor-icon-wrapper|campaign-cta-button-wrapper/i.test(
      html,
    )
  ) {
    return true
  }
  return false
}

const createEditorRemoveBtn = (isIcon) => {
  const removeBtn = document.createElement("button")
  removeBtn.className = isIcon ? "editor-icon-remove-btn" : "editor-image-remove-btn"
  removeBtn.type = "button"
  removeBtn.innerHTML = "×"
  removeBtn.contentEditable = "false"
  removeBtn.setAttribute("contenteditable", "false")
  removeBtn.onmousedown = (e) => {
    e.preventDefault()
    e.stopPropagation()
  }
  removeBtn.onclick = (e) => {
    e.preventDefault()
    e.stopPropagation()
    const wrapper = removeBtn.closest(".editor-image-wrapper, .editor-icon-wrapper")
    const editor = removeBtn.closest('[contenteditable="true"]')
    wrapper?.remove()
    editor?.dispatchEvent(new Event("input", { bubbles: true }))
  }
  return removeBtn
}

const wrapImgForEditor = (img, isIcon) => {
  if (img.closest(".editor-image-wrapper, .editor-icon-wrapper")) return

  const wrapper = document.createElement("div")
  wrapper.className = isIcon ? "editor-icon-wrapper" : "editor-image-wrapper"
  wrapper.contentEditable = "false"
  wrapper.setAttribute("contenteditable", "false")

  img.className = isIcon ? "editor-icon" : "editor-image"
  img.removeAttribute("data-campaign-media")
  img.contentEditable = "false"

  if (isIcon) {
    img.style.width = `${DEFAULT_ICON_SIZE_PX}px`
    img.style.height = `${DEFAULT_ICON_SIZE_PX}px`
    img.style.maxWidth = `${DEFAULT_ICON_SIZE_PX}px`
    img.style.maxHeight = `${DEFAULT_ICON_SIZE_PX}px`
    img.style.objectFit = "contain"
    img.style.display = "block"
    img.alt = "Icon"
  } else {
    img.style.maxWidth = img.style.maxWidth || "100%"
    img.style.height = "auto"
    img.style.display = "block"
    img.alt = img.alt || "Image"
  }

  const parent = img.parentNode
  if (!parent) return
  parent.insertBefore(wrapper, img)
  wrapper.appendChild(img)
  wrapper.appendChild(createEditorRemoveBtn(isIcon))
  delete wrapper.dataset.mediaReady
}

export const enrichHtmlForEditor = (html) => {
  if (!html) return ""

  try {
    // Do not coerce HTML source → markup here. Content step should keep
    // pasted/typed HTML source as text; only Preview/send render the design.
    const tempDiv = document.createElement("div")
    tempDiv.innerHTML = html

    normalizeCampaignButtonsForEditor(tempDiv)

    tempDiv.querySelectorAll("img").forEach((img) => {
      const icon =
        img.getAttribute("data-campaign-media") === CAMPAIGN_MEDIA_ICON ||
        img.classList.contains(CLASS_EMAIL_ICON) ||
        img.classList.contains("editor-icon")
      wrapImgForEditor(img, icon)
    })

    return tempDiv.innerHTML
  } catch {
    return html
  }
}

/** Preview uses the same HTML that is sent in email_block.text_en / text_ar */
export const prepareCampaignHtmlForDisplay = serializeDescriptionForEmail
